"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { gsap, PLAY_ONCE, useGSAP } from "@/lib/gsap";
import { reduceMotion } from "@/lib/motion";
import { Sheet, Sparkle } from "@/components/ui/icons";
import { QSH_PULSE } from "@/content/quiksheets";
import { cn } from "@/lib/utils";

type Phase = "idle" | "running" | "done";
const EASE = [0.23, 1, 0.32, 1] as const;

/** Presence reads the same way the product does: editing, commenting, just reading. */
const DOT: Record<string, string> = { Editing: "bg-mint", Commenting: "bg-amber-400" };

/**
 * A live pipeline tracker. Rows land in sequence, then the formula runs and the blended rate
 * resolves — with the row dragging it down called out. Auto-plays once in view, replayable.
 */
export function SheetMock() {
  const root = useRef<HTMLDivElement>(null);
  const [phase, setPhase] = useState<Phase>("idle");
  const timers = useRef<number[]>([]);
  const played = useRef(false);

  const clear = () => {
    timers.current.forEach(clearTimeout);
    timers.current = [];
  };

  const run = useCallback(() => {
    clear();
    played.current = true;
    setPhase("running");
    timers.current.push(window.setTimeout(() => setPhase("done"), 1400));
  }, []);

  const reset = () => {
    clear();
    setPhase("idle");
  };

  useEffect(() => clear, []);

  useGSAP(
    () => {
      const el = root.current;
      if (!el || reduceMotion()) return;
      const tl = gsap.timeline({ scrollTrigger: { trigger: el, start: "top 85%", toggleActions: PLAY_ONCE }, delay: 0.9 });
      tl.fromTo(
        gsap.utils.toArray("[data-mock-row]", el),
        { opacity: 0, y: 12 },
        { opacity: 1, y: 0, duration: 0.7, ease: "silk", stagger: 0.07 },
      );
      tl.call(() => {
        timers.current.push(window.setTimeout(() => !played.current && run(), 1600));
      });
    },
    { scope: root },
  );

  const running = phase === "running";
  const done = phase === "done";

  return (
    <div
      ref={root}
      className="w-full rounded-[22px] border border-white/[0.09] bg-[#0b0b0c] p-4 text-left text-white shadow-[0_40px_100px_-40px_rgba(0,0,0,0.9)] md:p-5"
    >
      <div data-mock-row="" className="flex items-center justify-between gap-3">
        <p className="truncate text-[11.5px] text-white/45">{QSH_PULSE.path}</p>
        <span
          className={cn(
            "shrink-0 rounded-full px-2.5 py-1 text-[10.5px] font-medium transition-colors duration-500",
            done ? "bg-mint text-ink" : "bg-mint/10 text-mint",
          )}
        >
          {done ? "Calculated" : "Live"}
        </span>
      </div>

      {/* The sheet, and who is in it */}
      <div data-mock-row="" className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-[14px] bg-white/[0.04] p-3.5">
        <div className="flex min-w-0 items-center gap-2.5">
          <span className="grid size-8 shrink-0 place-items-center rounded-[10px] bg-white/[0.07]">
            <Sheet className="size-4 text-mint" />
          </span>
          <div className="min-w-0">
            <p className="truncate text-[13.5px] font-medium">{QSH_PULSE.sheet.name}</p>
            <p className="mt-0.5 truncate text-[11px] text-white/45">{QSH_PULSE.sheet.meta}</p>
          </div>
        </div>
        <ul className="flex flex-wrap items-center gap-1.5">
          {QSH_PULSE.collaborators.map((c) => (
            <li key={c.name} className="flex items-center gap-1.5 rounded-full bg-white/[0.06] px-2.5 py-1 text-[11px] text-white/70">
              <span className={cn("size-1.5 shrink-0 rounded-full", DOT[c.state] ?? "bg-white/30")} />
              {c.name}
            </li>
          ))}
        </ul>
      </div>

      {/* The grid */}
      <div data-mock-row="" className="mt-2 overflow-hidden rounded-[14px] bg-white/[0.04]">
        <div className="grid grid-cols-[1.1fr_1fr_1fr_0.8fr] gap-2 border-b border-white/[0.06] px-3.5 py-2.5 text-[10.5px] font-medium uppercase tracking-[0.06em] text-white/40">
          {QSH_PULSE.columns.map((c) => (
            <span key={c} className={cn("truncate max-sm:whitespace-normal", c !== "Region" && "text-right")}>
              {c}
            </span>
          ))}
        </div>
        <ul>
          {QSH_PULSE.rows.map((r) => (
            <li key={r.cells[0]} className="grid grid-cols-[1.1fr_1fr_1fr_0.8fr] gap-2 border-b border-white/[0.04] px-3.5 py-2.5 text-[12.5px] last:border-b-0">
              {r.cells.map((cell, i) => (
                <span
                  key={cell}
                  className={cn(
                    "truncate tabular-nums",
                    i === 0 ? "text-white/80" : "text-right text-white/60",
                    i === 3 && (r.state === "down" ? "font-medium text-amber-400" : "font-medium text-mint"),
                  )}
                >
                  {cell}
                </span>
              ))}
            </li>
          ))}
        </ul>
      </div>

      {/* The formula bar */}
      <div data-mock-row="" className="mt-2 flex items-center gap-2.5 rounded-[14px] bg-white/[0.04] px-3.5 py-2.5">
        <span className="shrink-0 text-[11px] font-medium text-white/35">fx</span>
        <code className="truncate font-mono text-[12px] text-white/75">{QSH_PULSE.formula}</code>
      </div>

      <div className="mt-3 border-t border-white/[0.07] pt-3">
        <AnimatePresence mode="wait" initial={false}>
          {done ? (
            <motion.div
              key="result"
              initial={{ opacity: 0, y: 10, filter: "blur(6px)" }}
              animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
              transition={{ duration: 0.5, ease: EASE }}
            >
              <div className="flex items-baseline justify-between gap-3">
                <p className="text-[11px] font-medium uppercase tracking-[0.08em] text-white/45">{QSH_PULSE.result.label}</p>
                <p className="text-[20px] font-medium leading-none tracking-[-0.03em] text-mint">{QSH_PULSE.result.value}</p>
              </div>
              <p className="mt-3 text-[12.5px] leading-[1.5] text-white/80">{QSH_PULSE.footer}</p>
              <div className="mt-3 flex justify-end">
                <button type="button" onClick={reset} className="text-[11.5px] font-medium text-white/60 underline-offset-4 hover:text-white hover:underline">
                  Replay
                </button>
              </div>
            </motion.div>
          ) : (
            <motion.button
              key="cta"
              type="button"
              onClick={run}
              disabled={running}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3, ease: EASE }}
              className="flex w-full items-center justify-center gap-2 rounded-full bg-white/[0.06] py-2.5 text-[12.5px] font-medium text-white/85 transition-colors hover:bg-white/[0.1] disabled:opacity-60"
            >
              <Sparkle className={cn("size-3.5 shrink-0 text-mint", running && "animate-pulse")} />
              {running ? "Calculating…" : "Run the formula"}
            </motion.button>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
